/* Bienvenida primera compra: first-purchase signup dialog.
   - Auto-opens once for new visitors after a delay; a dismissal is
     remembered for N days, a successful signup forever.
   - Any link whose href contains "#bienvenida" opens it on demand (the
     announcement bar uses this); after a signup it reopens on the code.
   - The form is Shopify's native customer form, so a signup creates the
     customer record that makes a segment-limited discount valid at checkout.
     Shopify reloads the page after posting; a session flag set on submit
     tells this form's post apart from the footer newsletter's. */
if (!customElements.get('ilc-bienvenida')) {
  const STORE_KEY = 'ilc-bienvenida';
  const SUBMIT_KEY = 'ilc-bienvenida-submitted';

  const read = (storage, key) => {
    try {
      return JSON.parse(storage.getItem(key));
    } catch (e) {
      return null;
    }
  };
  const write = (storage, key, value) => {
    try {
      if (value === null) storage.removeItem(key);
      else storage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage blocked: the dialog still works, it just is not remembered */
    }
  };
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  class IlcBienvenida extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.form = this.querySelector('[data-bv-form]');
      this.email = this.querySelector('#ilc-bv-email');
      this.consent = this.querySelector('#ilc-bv-consent');
      this.submit = this.querySelector('[data-bv-submit]');
      this.state = read(localStorage, STORE_KEY) || {};

      this.querySelectorAll('[data-bv-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close(true))
      );
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close(true);
      });
      // Backdrop click: the dialog box itself is the only target outside the content.
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close(true);
      });

      this.form?.addEventListener('submit', (e) => this.onSubmit(e));
      this.email?.addEventListener('input', () => this.clearError('email'));
      this.consent?.addEventListener('change', () => this.clearError('consent'));
      this.querySelector('[data-bv-copy]')?.addEventListener('click', () => this.copy());

      this.onLinkClick = (e) => {
        const link = e.target.closest('a[href*="#bienvenida"]');
        if (!link) return;
        e.preventDefault();
        this.open(this.entryState());
      };
      document.addEventListener('click', this.onLinkClick);

      if (window.Shopify && window.Shopify.designMode) {
        this.setupEditor();
        return;
      }

      if (this.handlePostBack()) return;
      // Shared links and bar clicks that beat the deferred script land here.
      if (location.hash === '#bienvenida') {
        this.open(this.entryState());
        return;
      }
      this.scheduleAuto();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onLinkClick);
      clearTimeout(this.timer);
    }

    entryState() {
      // A customer with orders can no longer use the code, even if they claimed it.
      if (this.dataset.hasOrders === 'true') return 'returning';
      return this.state.claimed ? 'success' : 'offer';
    }

    /* After Shopify reloads the page from our form's POST. */
    handlePostBack() {
      const submitted = read(sessionStorage, SUBMIT_KEY);
      if (!submitted) return false;
      const params = new URLSearchParams(location.search);
      const posted = params.get('customer_posted') === 'true' || this.querySelector('[data-bv-posted]');
      const failed = this.querySelector('[data-bv-server-error]');
      if (!posted && !failed) return false;

      write(sessionStorage, SUBMIT_KEY, null);
      if (posted && !failed) {
        this.state = { claimed: true, at: Date.now() };
        write(localStorage, STORE_KEY, this.state);
        this.open('success');
        // Drop ?customer_posted so a refresh does not replay the state.
        params.delete('customer_posted');
        const qs = params.toString();
        history.replaceState(null, '', location.pathname + (qs ? `?${qs}` : ''));
      } else {
        this.email?.setAttribute('aria-invalid', 'true');
        this.open('offer');
        this.email?.focus();
      }
      return true;
    }

    scheduleAuto() {
      if (this.dataset.auto !== 'true' || this.state.claimed) return;
      const days = parseInt(this.dataset.dismissDays, 10) || 14;
      if (this.state.dismissedAt && Date.now() - this.state.dismissedAt < days * 864e5) return;

      const delay = (parseInt(this.dataset.delay, 10) || 8) * 1000;
      // Warm the photo shortly before the dialog needs it.
      const img = this.querySelector('.ilc-bv__img');
      setTimeout(() => img && (img.loading = 'eager'), Math.max(delay - 2500, 0));
      this.timer = setTimeout(() => {
        // Do not stack over another open dialog or drawer.
        if (document.querySelector('dialog[open]') || document.body.classList.contains('sidebar-opened')) return;
        this.open('offer');
      }, delay);
    }

    setupEditor() {
      const id = this.dataset.sectionId;
      const preview = this.dataset.preview || 'offer';
      document.addEventListener('shopify:section:select', (e) => {
        if (e.detail.sectionId === id) this.open(preview);
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.detail.sectionId === id) this.close(false);
      });
    }

    open(which) {
      clearTimeout(this.timer);
      this.show(which);
      if (!this.dialog.open) {
        this.lastFocus = document.activeElement;
        this.querySelector('.ilc-bv__img')?.setAttribute('loading', 'eager');
        this.dialog.showModal();
      }
      if (which !== 'offer') this.querySelector(`#ilc-bv-title-${which}`)?.focus();
    }

    show(which) {
      this.querySelectorAll('[data-bv-state]').forEach((el) => {
        el.hidden = el.dataset.bvState !== which;
      });
      this.dialog.setAttribute('aria-labelledby', `ilc-bv-title-${which}`);
    }

    close(remember) {
      if (!this.dialog.open) return;
      this.dialog.close();
      if (remember && !this.state.claimed) {
        this.state = { ...this.state, dismissedAt: Date.now() };
        write(localStorage, STORE_KEY, this.state);
      }
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
    }

    onSubmit(e) {
      const email = (this.email?.value || '').trim();
      let firstBad = null;
      if (!EMAIL_RE.test(email)) {
        this.setError('email', email ? 'Ese correo no parece válido. Revísalo, por ejemplo nombre@correo.com.' : 'Escribe tu correo para recibir el código.');
        firstBad = firstBad || this.email;
      }
      if (!this.consent?.checked) {
        this.setError('consent');
        firstBad = firstBad || this.consent;
      }
      if (firstBad) {
        e.preventDefault();
        firstBad.focus();
        return;
      }
      if (this.email) this.email.value = email;
      write(sessionStorage, SUBMIT_KEY, true);
      this.submit?.setAttribute('aria-busy', 'true');
      const label = this.querySelector('[data-bv-submit-label]');
      if (label) label.textContent = 'Enviando…';
    }

    setError(field, message) {
      const box = this.querySelector(`[data-bv-error="${field}"]`);
      const input = field === 'email' ? this.email : this.consent;
      if (message) {
        const text = box?.querySelector('[data-bv-error-text]');
        if (text) text.textContent = message;
      }
      if (box) box.hidden = false;
      input?.setAttribute('aria-invalid', 'true');
    }

    clearError(field) {
      const box = this.querySelector(`[data-bv-error="${field}"]`);
      const input = field === 'email' ? this.email : this.consent;
      if (box) box.hidden = true;
      input?.removeAttribute('aria-invalid');
    }

    async copy() {
      const btn = this.querySelector('[data-bv-copy]');
      const code = this.querySelector('[data-bv-code]');
      const label = this.querySelector('[data-bv-copy-label]');
      const live = this.querySelector('[data-bv-live]');
      let ok = false;
      try {
        await navigator.clipboard.writeText(code.textContent.trim());
        ok = true;
      } catch (e) {
        const range = document.createRange();
        range.selectNodeContents(code);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        ok = document.execCommand && document.execCommand('copy');
      }
      if (!ok) return;
      btn.classList.add('is-copied');
      label.textContent = 'Copiado';
      live.textContent = 'Código copiado';
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        btn.classList.remove('is-copied');
        label.textContent = 'Copiar código';
        live.textContent = '';
      }, 2400);
    }
  }

  customElements.define('ilc-bienvenida', IlcBienvenida);
}
